<template>
  <model-form
    ref="modelForm"
    :model="membershipPoint"
    :title="`${membershipPoint.membershipTypeId ? `Edit ${membershipPoint.name} for ${membershipType.name}` : `Add ${$_.startCase(currentCircuit.rewardsCurrencyName)} to ${membershipType.name}`}`"
    :submodel="true"
    modal-type="modal"
  >
    <div class="column">
      <div class="row q-col-gutter-x-sm items-center">
        <q-input
          v-model="membershipPoint.name"
          class="col-9"
          label="Name"
          :rules="[val => !!val || 'Field is required']"
          bottom-slots
        />
        <q-toggle
          v-model="membershipPoint.enable"
          class="col-3 q-pb-md"
          label="Enable"
          bottom-slots
        >
          <q-tooltip>
            Disable this benefit to prevent members earning these {{ $_.startCase(currentCircuit.rewardsCurrencyName) }}.
          </q-tooltip>
        </q-toggle>
      </div>
      <div class="row q-col-gutter-x-sm items-center">
        <model-form-select
          field="pointsType"
          class="col-6"
          label="Points Type"
          :options="$mh.asOptions(POINTS_TYPES.ALL)"
          map-options
          emit-value
          :rules="[val => !!val || 'Field is required']"
        >
          <template #option="scope">
            <q-item
              v-bind="scope.itemProps"
              v-on="scope.itemEvents"
            >
              <q-item-section>
                <div class="row items-center justify-between q-gutter-x-sm">
                  <span>{{ scope.opt.label }}</span>
                  <q-icon
                    name="info"
                    size="sm"
                  >
                    <q-tooltip>
                      {{ POINTS_TYPES_TOOLTIPS[scope.opt.value] }}
                    </q-tooltip>
                  </q-icon>
                </div>
              </q-item-section>
            </q-item>
          </template>
          <q-tooltip>
            Compute points earned based on different purchases or events.
          </q-tooltip>
        </model-form-select>
        <q-input
          v-model.number="membershipPoint.pointsRate"
          class="col-6"
          :label="`${$_.startCase(membershipPoint.pointsType)} Points Rate`"
          type="number"
          step="1"
          min="1"
          :rules="[
            val => !!val || 'Field is required',
            val => !!val || 'Points must be a number',
            val => val > 0 || 'Points must be greater than 0'
          ]"
          bottom-slots
        >
          <q-tooltip>
            Points earned on each order.
          </q-tooltip>
        </q-input>
      </div>
      <div
        class="row q-col-gutter-x-sm items-center"
      >
        <q-toggle
          v-model="membershipPoint.expire"
          class="col-4 q-pb-md"
          label="Expire"
          bottom-slots
        />
        <q-input
          v-model.number="membershipPoint.expires"
          :disable="!membershipPoint.expire"
          class="col-4"
          label="Expire After"
          type="number"
          step="1"
          min="1"
          :max="maximumRenews"
          :rules="[val => !!val && val > 0 && val <= maximumRenews || ('Field cannot exceed ' + maximumRenews)]"
          bottom-slots
        />
        <q-select
          v-model="membershipPoint.expiryTerm"
          :disable="!membershipPoint.expire"
          class="col-4 text-capitalize"
          label="Expiry Term"
          :options="$mh.asOptions(MEMBERSHIP_POINT_EXPIRY_TERMS.ALL)"
          map-options
          emit-value
          :rules="[val => val !== null || 'Field is required']"
          bottom-slots
        />
        <model-form-select
          field="conditions"
          class="col-12 q-mt-md"
          label="Conditions"
          :options="$mh.asOptions(CONDITIONS[membershipPoint.pointsType])"
          map-options
          emit-value
          multiple
          use-chips
          clearable
          :decode="conditions => $mh.objectToArray(conditions)"
          :encode="enabledConditions => $mh.arrayToObject(enabledConditions || [], { allKeys: CONDITIONS.ALL })"
        >
          <template #option="scope">
            <q-item
              v-bind="scope.itemProps"
              v-on="scope.itemEvents"
            >
              <q-item-section>
                <div class="row items-center justify-between q-gutter-x-sm">
                  <span>{{ scope.opt.label }}</span>
                  <q-icon
                    name="info"
                    size="sm"
                  >
                    <q-tooltip>
                      {{ CONDITIONS_TOOLTIPS[scope.opt.value] }}
                    </q-tooltip>
                  </q-icon>
                </div>
              </q-item-section>
            </q-item>
          </template>
          <q-tooltip>
            Set conditions to be met for members to earn these points. If multiple conditions are selected, all must be met to earn points.
          </q-tooltip>
        </model-form-select>
      </div>
      <div
        class="row q-col-gutter-x-sm items-center"
      >
        <dates-condition-form
          v-if="membershipPoint.conditions[CONDITIONS.DATES]"
          :conditional="membershipPoint"
        />

        <date-range-condition-form
          v-if="membershipPoint.conditions[CONDITIONS.DATE_RANGE]"
          :conditional="membershipPoint"
        />

        <day-of-week-condition-form
          v-if="membershipPoint.conditions[CONDITIONS.DAY_OF_WEEK]"
          :conditional="membershipPoint"
        />

        <discounts-condition-form
          v-if="membershipPoint.conditions[CONDITIONS.DISCOUNTS]"
          :conditional="membershipPoint"
        />

        <items-condition-form
          v-if="membershipPoint.conditions[CONDITIONS.ITEMS]"
          :conditional="membershipPoint"
        />

        <item-classes-condition-form
          v-if="membershipPoint.conditions[CONDITIONS.ITEM_CLASSES]"
          :conditional="membershipPoint"
        />

        <item-packages-condition-form
          v-if="membershipPoint.conditions[CONDITIONS.ITEM_PACKAGES]"
          :conditional="membershipPoint"
        />

        <longevity-condition-form
          v-if="membershipPoint.conditions[CONDITIONS.LONGEVITY]"
          :conditional="membershipPoint"
        />

        <price-condition-form
          v-if="membershipPoint.conditions[CONDITIONS.PRICE]"
          :conditional="membershipPoint"
        />

        <movies-condition-form
          v-if="membershipPoint.conditions[CONDITIONS.MOVIES]"
          :conditional="membershipPoint"
        />

        <showing-badges-condition-form
          v-if="membershipPoint.conditions[CONDITIONS.SHOWING_BADGES]"
          :conditional="membershipPoint"
        />

        <dates-condition-form
          v-if="membershipPoint.conditions[CONDITIONS.SHOWING_DATES]"
          name="ShowingDatesConditionForm"
          :conditional="membershipPoint"
          :dates-field="CONDITION_REQUIREMENTS.SHOWING_DATES"
          :exclude-dates-field="CONDITION_REQUIREMENTS.EXCLUDE_SHOWING_DATES"
        />

        <date-range-condition-form
          v-if="membershipPoint.conditions[CONDITIONS.SHOWING_DATE_RANGE]"
          name="ShowingDateRangeConditionForm"
          :conditional="membershipPoint"
          :date-range-start-field="CONDITION_REQUIREMENTS.SHOWING_DATE_RANGE_START"
          :date-range-end-field="CONDITION_REQUIREMENTS.SHOWING_DATE_RANGE_END"
          :exclude-date-range-field="CONDITION_REQUIREMENTS.EXCLUDE_SHOWING_DATE_RANGE"
        />

        <day-of-week-condition-form
          v-if="membershipPoint.conditions[CONDITIONS.SHOWING_DAY_OF_WEEK]"
          name="ShowingDayOfWeekConditionForm"
          :conditional="membershipPoint"
          :day-of-week-field="CONDITION_REQUIREMENTS.SHOWING_DAY_OF_WEEK"
          :exclude-day-of-week-field="CONDITION_REQUIREMENTS.EXCLUDE_SHOWING_DAY_OF_WEEK"
        />

        <time-of-day-condition-form
          v-if="membershipPoint.conditions[CONDITIONS.SHOWING_TIME_OF_DAY]"
          name="ShowingTimeOfDayConditionForm"
          :conditional="membershipPoint"
          :time-of-day-start-field="CONDITION_REQUIREMENTS.SHOWING_TIME_OF_DAY_START"
          :time-of-day-end-field="CONDITION_REQUIREMENTS.SHOWING_TIME_OF_DAY_END"
          :exclude-time-of-day-field="CONDITION_REQUIREMENTS.EXCLUDE_SHOWING_TIME_OF_DAY"
        />

        <sites-condition-form
          v-if="membershipPoint.conditions[CONDITIONS.SITES]"
          :conditional="membershipPoint"
        />

        <tier-condition-form
          v-if="membershipPoint.conditions[CONDITIONS.TIER]"
          :conditional="membershipPoint"
          :membership-tiers="membershipType.membershipTiers.data"
        />

        <time-of-day-condition-form
          v-if="membershipPoint.conditions[CONDITIONS.TIME_OF_DAY]"
          :conditional="membershipPoint"
        />

        <title-classes-condition-form
          v-if="membershipPoint.conditions[CONDITIONS.TITLE_CLASSES]"
          :conditional="membershipPoint"
        />

        <voucher-types-condition-form
          v-if="membershipPoint.conditions[CONDITIONS.VOUCHERS]"
          :conditional="membershipPoint"
        />
      </div>
    </div>
  </model-form>
</template>

<script>
import { CONDITION_REQUIREMENTS, CONDITIONS, MEMBERSHIP_POINT_EXPIRY_TERMS, POINTS_TYPES } from 'src/models/membership-type/MembershipPoint'
import MembershipType from 'src/models/membership-type/MembershipType'
import DateRangeConditionForm from 'src/modules/admin/components/condition-requirement-forms/DateRangeConditionForm'
import DatesConditionForm from 'src/modules/admin/components/condition-requirement-forms/DatesConditionForm'
import DayOfWeekConditionForm from 'src/modules/admin/components/condition-requirement-forms/DayOfWeekConditionForm'
import DiscountsConditionForm from 'src/modules/admin/components/condition-requirement-forms/DiscountsConditionForm'
import ItemClassesConditionForm from 'src/modules/admin/components/condition-requirement-forms/ItemClassesConditionForm'
import ItemPackagesConditionForm from 'src/modules/admin/components/condition-requirement-forms/ItemPackagesConditionForm'
import ItemsConditionForm from 'src/modules/admin/components/condition-requirement-forms/ItemsConditionForm'
import LongevityConditionForm from 'src/modules/admin/components/condition-requirement-forms/LongevityConditionForm'
import MoviesConditionForm from 'src/modules/admin/components/condition-requirement-forms/MoviesConditionForm'
import PriceConditionForm from 'src/modules/admin/components/condition-requirement-forms/PriceConditionForm'
import ShowingBadgesConditionForm from 'src/modules/admin/components/condition-requirement-forms/ShowingBadgesConditionForm'
import SitesConditionForm from 'src/modules/admin/components/condition-requirement-forms/SitesConditionForm'
import TierConditionForm from 'src/modules/admin/components/condition-requirement-forms/TierConditionForm'
import TimeOfDayConditionForm from 'src/modules/admin/components/condition-requirement-forms/TimeOfDayConditionForm'
import TitleClassesConditionForm from 'src/modules/admin/components/condition-requirement-forms/TitleClassesConditionForm'
import VoucherTypesConditionForm from 'src/modules/admin/components/condition-requirement-forms/VoucherTypesConditionForm'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'

const POINTS_TYPES_TOOLTIPS = {
  [POINTS_TYPES.BIRTHDAY]: 'Points earned on the members birthday. Assigned maximum of once annually.',
  [POINTS_TYPES.VISIT]: 'Points earned on each order.',
  [POINTS_TYPES.CONCESSION_PRICE]: 'Points earned on each dollar spent on concessions.',
  [POINTS_TYPES.TICKET_PURCHASE]: 'Points earned on each ticket purchased.',
  [POINTS_TYPES.TICKET_VALUE]: 'Points earned on each dollar spent on tickets.',
  [POINTS_TYPES.TICKET_PRICE]: 'Points earned on each dollar value of purchased tickets, before vouchers or discounts are applied.',
  [POINTS_TYPES.MEMBERSHIP_PURCHASE]: 'Points earned on membership purchases, including renewals or new account registrations for default membership types.'
}

const CONDITIONS_TOOLTIPS = {
  [CONDITIONS.BIRTHDAY]: 'Points will be earned by active members on their birthday.',
  [CONDITIONS.BIRTHWEEK]: 'Points will be earned by active members during their birthweek.',
  [CONDITIONS.DATES]: 'Points will be earned by active members on the given dates.',
  [CONDITIONS.DATE_RANGE]: 'Points will be earned by active members during the given date range.',
  [CONDITIONS.DAY_OF_WEEK]: 'Points will be earned by active members on the given day of the week.',
  [CONDITIONS.DISCOUNTS]: 'Points will be earned on order items including or excluding given discounts.',
  [CONDITIONS.ITEMS]: 'Points will be earned on order items including or excluding the given items.',
  [CONDITIONS.ITEM_CLASSES]: 'Points will be earned on order items including or excluding the given item classes.',
  [CONDITIONS.ITEM_PACKAGES]: 'Points will be earned on order items including or excluding the given item packages.',
  [CONDITIONS.LONGEVITY]: 'Points will be earned by active members who have maintained their membership for the given interval.',
  [CONDITIONS.MOVIES]: 'Points will be earned on tickets for showings including or excluding the given movies.',
  [CONDITIONS.PRICE]: 'Points will be earned on order items within the given (inclusive) price range.',
  [CONDITIONS.SHOWING_BADGES]: 'Points will be earned by active members attending showings with the given showing badges (e.g. Premium, 3D).',
  [CONDITIONS.SHOWING_BIRTHDAY]: 'Points will be earned by active members attending showings on their birthday.',
  [CONDITIONS.SHOWING_BIRTHWEEK]: 'Points will be earned by active members attending showings during their birthweek.',
  [CONDITIONS.SHOWING_DATES]: 'Points will be earned by active members attending showings for the given dates.',
  [CONDITIONS.SHOWING_DATE_RANGE]: 'Points will be earned by active members attending showings within the given date range.',
  [CONDITIONS.SHOWING_DAY_OF_WEEK]: 'Points will be earned by active members attending showings on the given day of the week.',
  [CONDITIONS.SHOWING_TIME_OF_DAY]: 'Points will be earned for showings within the given time range on applicable days.',
  [CONDITIONS.SITES]: 'Points will be earned on orders for the given sites.',
  [CONDITIONS.TIER]: 'Points will be earned by active members of the given tier.',
  [CONDITIONS.TIME_OF_DAY]: 'Points will be earned for purchases within the given time range on applicable days.',
  [CONDITIONS.TITLE_CLASSES]: 'Points will be earned by active members attending showings with the given title classes.',
  [CONDITIONS.VOUCHERS]: 'Points will be earned on order items including or excluding given vouchers.'
}

export default {
  name: 'MembershipPointForm',
  components: {
    ModelForm,
    DateRangeConditionForm,
    DatesConditionForm,
    DayOfWeekConditionForm,
    DiscountsConditionForm,
    ItemClassesConditionForm,
    ItemPackagesConditionForm,
    ItemsConditionForm,
    LongevityConditionForm,
    MoviesConditionForm,
    PriceConditionForm,
    ShowingBadgesConditionForm,
    SitesConditionForm,
    TierConditionForm,
    TimeOfDayConditionForm,
    TitleClassesConditionForm,
    VoucherTypesConditionForm
  },
  configuration: ['currentCircuit'],
  props: {
    membershipType: {
      type: MembershipType,
      required: true
    }
  },
  models: {
    membershipPoint: {
      membershipType: 'default',
      discounts: {
        fields: ['id', 'name']
      },
      membershipTiers: {
        fields: ['id', 'name']
      },
      membershipTierIds: 'default',
      siteItems: {
        fields: ['id', 'name']
      },
      siteItemIds: 'default',
      itemClasses: {
        fields: ['id', 'name']
      },
      siteItemPackages: {
        fields: ['id', 'name']
      },
      siteItemPackageIds: 'default',
      siteMovies: {
        fields: ['id', 'name']
      },
      siteMovieIds: 'default',
      showingBadges: {
        fields: ['id', 'name']
      },
      titleClasses: {
        fields: ['id', 'name']
      },
      voucherTypes: {
        fields: ['id', 'name']
      }
    }
  },
  computed: {
    maximumRenews () {
      return this.membershipPoint.renewalTerm === MEMBERSHIP_POINT_EXPIRY_TERMS.YEARS ? 5 : (5 * 12)
    }
  },
  watch: {
    'membershipPoint.pointsType' () {
      const excludedConditions = _.without(CONDITIONS.ALL, ...CONDITIONS[this.membershipPoint.pointsType])

      _.each(excludedConditions, condition => { this.membershipPoint.conditions[condition] = false })
    }
  },
  created () {
    _.extend(this, {
      POINTS_TYPES,
      POINTS_TYPES_TOOLTIPS,
      MEMBERSHIP_POINT_EXPIRY_TERMS,
      CONDITIONS,
      CONDITION_REQUIREMENTS,
      CONDITIONS_TOOLTIPS
    })
  }
}

</script>
